/**
 * 근거 응답 제출(총괄 DEV-I-010, 디자인 상태표 EV-S). 배정 하나 단위. DOM 없이 단위 테스트한다.
 *
 * - 요청 키는 학생이 '제출하기'를 누를 때 한 번 만든다. 미확정 동안의 재전송·'다시 확인'·재로그인 뒤 확인은 같은 키·같은 보기만 쓴다.
 * - 4초가 지나면 '확인 중' 표시만 바꾼다. 원래 POST가 진행 중이면 새 POST를 보내지 않는다(동시에 하나).
 * - 응답 유실·연결 실패·5xx·request_in_unknown_state: 미확정. 같은 키로 1.5·3·6·8초 간격 재전송, 확인 창(30초) 안에서만.
 *   창이 지나면 잠금을 유지하고 '다시 확인'(같은 키)을 기다린다.
 * - request_key_reused(같은 키 다른 내용): 입력 오류·제출 실패로 정하지 않는다. 자동 재전송·새 키를 멈추고 서버 상태 조회만 한다.
 * - 확정은 ① 제출 응답(200·409 already_answered) ② 조회의 answered로만. 조회의 open·보류·취소만으로 실패를 정하지 않는다.
 * - POST에는 취소 신호를 넘기지 않는다. 화면을 떠나도 기록·타이머는 탭에 남는다. 계정이 바뀌면 clear().
 * - 응답 반영 조건: 그 배정의 기록이 같은 기록(교체·정리되지 않음)이고 기록의 계정이 지금 계정.
 */
import type { ApiResult, Transport } from './transport.js';
import type { SubmitTiming } from './submit-controller.js';

export type PendingPhase = 'sending' | 'uncertain' | 'stalled' | 'key_conflict';
export interface PendingSubmit {
  readonly assignmentId: string;
  readonly attemptId: string;
  readonly key: string;
  readonly optionId: string;
  readonly owner: string;
  phase: PendingPhase;
  t0: number;
  tries: number;
  inflight: boolean;
  timers: unknown[];
}

/** 거부(제출 안 됨) 안내 종류 */
export type RejectNote = 'rejected' | 'reprovide' | 'reselect';
export type ConfirmKind = 'ok' | 'already';

export interface EvidenceSubmitDeps {
  readonly transport: Transport;
  readonly newKey: () => string;
  readonly timing: SubmitTiming;
  readonly now: () => number;
  readonly setTimeout: (fn: () => void, ms: number) => unknown;
  readonly clearTimeout: (id: unknown) => void;
  /** 지금 로그인한 계정 ID */
  readonly me: () => string | null;
  /** 화면 다시 그리기 */
  readonly onChange: (assignmentId: string) => void;
  readonly onConfirmed: (p: PendingSubmit, kind: ConfirmKind) => void;
  readonly onRejected: (p: PendingSubmit, note: RejectNote) => void;
  /** 미확정: 서버 상태 확인 조회를 요청(앱이 진행 중 조회가 없을 때만 보냄) */
  readonly onCheck: (p: PendingSubmit) => void;
  readonly onRelogin: (p: PendingSubmit) => void;
  readonly onAccess: (p: PendingSubmit, r: Extract<ApiResult, { kind: 'http' }>) => void;
  readonly onDevlog?: (code: string, detail: string) => void;
}

const STATE_REJECT = new Set(['assignment_on_hold', 'question_unavailable', 'assignment_cancelled', 'assignment_not_open']);

export class EvidenceSubmitter {
  private readonly pend = new Map<string, PendingSubmit>();
  constructor(private readonly d: EvidenceSubmitDeps) {}

  get(assignmentId: string): PendingSubmit | undefined { return this.pend.get(assignmentId); }
  all(): PendingSubmit[] { return [...this.pend.values()]; }

  /** '제출하기': 새 키 하나. 이미 미확정 기록이 있으면 아무것도 하지 않는다 */
  start(assignmentId: string, attemptId: string, optionId: string): PendingSubmit | null {
    const owner = this.d.me();
    if (owner === null || this.pend.has(assignmentId)) return null;
    const p: PendingSubmit = { assignmentId, attemptId, key: this.d.newKey(), optionId, owner, phase: 'sending', t0: this.d.now(), tries: 0, inflight: false, timers: [] };
    this.pend.set(assignmentId, p);
    this.d.onChange(assignmentId);
    void this.drive(assignmentId);
    return p;
  }

  private live(p: PendingSubmit): boolean { return this.pend.get(p.assignmentId) === p && this.d.me() === p.owner; }
  private later(p: PendingSubmit, ms: number, fn: () => void): void {
    const box: { id: unknown } = { id: null };
    box.id = this.d.setTimeout(() => { p.timers = p.timers.filter((x) => x !== box.id); if (this.live(p)) fn(); }, ms);
    p.timers.push(box.id);
  }
  /** 보내는 중도 아니고 예약된 재전송·표시 타이머도 없음(화면 복귀 때 이어서 확인할 대상) */
  idle(assignmentId: string): boolean { const p = this.pend.get(assignmentId); return p !== undefined && !p.inflight && p.timers.length === 0; }
  private drop(p: PendingSubmit): void {
    for (const t of p.timers) this.d.clearTimeout(t);
    p.timers = [];
    if (this.pend.get(p.assignmentId) === p) this.pend.delete(p.assignmentId);
  }

  /** 같은 키·같은 보기로 한 번 보낸다(진행 중이면 보내지 않음) */
  async drive(assignmentId: string): Promise<void> {
    const p = this.pend.get(assignmentId);
    if (p === undefined || p.inflight || p.phase === 'key_conflict') return;
    p.inflight = true; p.tries++;
    if (p.phase === 'stalled') p.phase = 'uncertain';
    this.d.onChange(assignmentId);
    // 4초: 표시만 '확인 중'으로. 새 요청은 보내지 않는다
    this.later(p, this.d.timing.displayWaitMs, () => { if (p.phase === 'sending') { p.phase = 'uncertain'; this.d.onChange(assignmentId); } });
    // 확인 창: 원래 요청이 끝나지 않아도 창이 지나면 '확인 못 함'(잠금 유지, 다시 확인 대기)
    const remain = Math.max(0, p.t0 + this.d.timing.windowMs - this.d.now());
    this.later(p, remain, () => { if (p.phase !== 'key_conflict') { p.phase = 'stalled'; this.d.onChange(assignmentId); } });
    const r = await this.d.transport.request('POST', `/attempts/${p.attemptId}/evidence/${assignmentId}/answer`, { key: p.key, body: { selectedOptionId: p.optionId } });
    p.inflight = false;
    if (!this.live(p)) return; // 정리됐거나 계정이 바뀜: 늦은 응답은 반영하지 않음
    for (const t of p.timers) this.d.clearTimeout(t);
    p.timers = [];
    const err = r.kind === 'http' ? (r.body['error'] as string | undefined) : undefined;
    if (r.kind === 'http' && r.status === 200) { this.drop(p); this.d.onConfirmed(p, 'ok'); return; }
    if (r.kind === 'http' && r.status === 409 && err === 'already_answered') { this.drop(p); this.d.onConfirmed(p, 'already'); return; }
    if (r.kind === 'http' && r.status === 409 && err !== undefined && STATE_REJECT.has(err)) { this.drop(p); this.d.onRejected(p, 'rejected'); return; }
    if (r.kind === 'http' && r.status === 409 && err === 'question_not_provided') { this.drop(p); this.d.onRejected(p, 'reprovide'); return; }
    if (r.kind === 'http' && ((r.status === 400 && err === 'option_required') || (r.status === 422 && err === 'option_not_in_question'))) { this.drop(p); this.d.onRejected(p, 'reselect'); return; }
    if (r.kind === 'http' && ((r.status === 409 && err === 'request_key_reused') || (r.status === 400 && err === 'invalid_request_key'))) {
      // 같은 키로 다른 내용이 이미 있음: 제출 실패로 정하지 않고 멈춘 뒤 서버 상태만 확인
      p.phase = 'key_conflict';
      this.d.onDevlog?.('request_key_reused', `${assignmentId}`);
      this.d.onChange(assignmentId); this.d.onCheck(p); return;
    }
    if (r.kind === 'http' && r.status === 401) { p.phase = 'uncertain'; this.d.onChange(assignmentId); this.d.onRelogin(p); return; }
    if (r.kind === 'http' && (r.status === 403 || r.status === 404)) { this.drop(p); this.d.onAccess(p, r); return; }
    // 연결 실패·5xx·request_in_unknown_state·그 밖: 미확정. 같은 키로 다시 보냄(창 안에서만)
    this.d.onDevlog?.('submit_uncertain', `${assignmentId} ${r.kind === 'http' ? `${r.status} ${err ?? ''}` : 'network'}`);
    const elapsed = this.d.now() - p.t0;
    if (elapsed >= this.d.timing.windowMs) { p.phase = 'stalled'; this.d.onChange(assignmentId); return; }
    p.phase = 'uncertain';
    this.d.onChange(assignmentId);
    this.d.onCheck(p);
    const delays = this.d.timing.resendDelaysMs;
    const wait = delays[Math.min(p.tries - 1, delays.length - 1)]!;
    if (elapsed + wait >= this.d.timing.windowMs) {
      this.later(p, this.d.timing.windowMs - elapsed, () => { p.phase = 'stalled'; this.d.onChange(assignmentId); });
      return;
    }
    this.later(p, wait, () => void this.drive(assignmentId));
  }

  /** '다시 확인'(학생): 확인 창을 새로 시작해 같은 키로 다시 보낸다. 키 충돌이면 조회만, 원래 요청이 진행 중이면 조회만 */
  recheck(assignmentId: string): void {
    const p = this.pend.get(assignmentId);
    if (p === undefined) return;
    if (p.phase === 'key_conflict' || p.inflight) { this.d.onCheck(p); return; }
    p.t0 = this.d.now(); p.phase = 'uncertain';
    void this.drive(assignmentId);
  }

  /** 같은 계정 재로그인 뒤: 진행 중이 아닌 미확정 기록을 같은 키로 이어서 확인 */
  resumeAll(): void {
    for (const p of this.all()) {
      if (p.inflight || p.phase === 'key_conflict' || this.d.me() !== p.owner) continue;
      p.t0 = this.d.now();
      void this.drive(p.assignmentId);
    }
  }

  /** 조회가 answered를 보여 줌: 제출 확정(어느 기기의 제출이든 서버 기록이 우선) */
  confirmByQuery(assignmentId: string): PendingSubmit | null {
    const p = this.pend.get(assignmentId);
    if (p === undefined) return null;
    this.drop(p);
    return p;
  }

  /**
   * (DEV-I-011) 키 충돌 기록을 서버 상태에 맡김: 조회가 보류·사용 중단·취소를 보여 줄 때만 쓴다(그 상태 카드를 따름).
   * 제출 실패를 확정하는 것이 아니며, open이면 쓰지 않는다(잠금 유지)
   */
  discardConflict(assignmentId: string): boolean {
    const p = this.pend.get(assignmentId);
    if (p === undefined || p.phase !== 'key_conflict') return false;
    this.drop(p);
    return true;
  }

  /** 로그아웃·다른 계정: 모든 기록·타이머 정리(서버 기록은 그대로) */
  clear(): void { for (const p of this.all()) this.drop(p); }
}
